/**
 * Incluir projeto — o "Incluir" do menu Projeto do Sistema de hoje.
 *
 * A API sempre teve o `POST api/projetos`; faltava a porta. São os mesmos dois
 * campos do cadastro antigo (descrição e categoria), mais a observação, que lá
 * era um segundo passo ("Editar Observação") e aqui cabe no mesmo gesto.
 *
 * Criado, o projeto abre na aba **Projeto**: um projeto novo não tem pinout nem
 * diagrama, e o que falta primeiro é o que ele vende (produtos) e quem trabalha
 * nele — é lá que isso se faz.
 */

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto } from "@/api/cliente"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import type { ProjetoResponse } from "@/api/tipos"
import { CATEGORIAS } from "./categorias"

export function NovoProjeto({ aoFechar }: { aoFechar: () => void }) {
  const navegar = useNavigate()
  const qc = useQueryClient()
  const [descricao, setDescricao] = useState("")
  const [categoria, setCategoria] = useState<string>(CATEGORIAS[0])
  const [obs, setObs] = useState("")

  const criar = useMutation({
    mutationFn: async () =>
      (await api.post<ProjetoResponse>("api/projetos",
        { descricao: descricao.trim(), categoria, obs: obs.trim() }, foraDoProjeto())).data,
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ["projetos"] })
      navegar(`/projetos/${p.id}/projeto`)
    },
  })

  const pode = descricao.trim().length > 0 && !criar.isPending
  const erro = criar.error instanceof ErroDeTrava || criar.error instanceof ErroDaApi
    ? criar.error.message
    : criar.error ? "Não foi possível criar o projeto." : null

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (pode) criar.mutate() }}
      aria-label="Novo projeto"
      style={{
        flex: "0 0 auto", display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 10,
        padding: "12px 18px", background: "var(--surface)", borderBottom: "1px solid var(--line2)",
      }}
    >
      <Campo rotulo="Descrição" largura={360}>
        <input
          className="campo" value={descricao} autoFocus required
          onChange={(e) => setDescricao(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") aoFechar() }}
          placeholder="Ex.: Vw Voyage 1.0 #2022-23#"
          style={entrada}
        />
      </Campo>
      <Campo rotulo="Categoria" largura={160}>
        <select className="campo" value={categoria} onChange={(e) => setCategoria(e.target.value)}
                style={entrada}>
          {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Campo>
      <Campo rotulo="Observação (opcional)" largura={300}>
        <input className="campo" value={obs} onChange={(e) => setObs(e.target.value)} style={entrada} />
      </Campo>

      <button
        type="submit" disabled={!pode}
        style={{
          height: 32, padding: "0 14px", borderRadius: 7, fontSize: 12.5, fontWeight: 600, border: 0,
          background: pode ? "var(--brand)" : "var(--surface2)", color: pode ? "#FFFFFF" : "var(--txt3)",
          cursor: pode ? "pointer" : "default",
        }}
      >
        {criar.isPending ? "Criando…" : "Criar projeto"}
      </button>
      <button
        type="button" onClick={aoFechar} disabled={criar.isPending}
        style={{
          height: 32, padding: "0 12px", borderRadius: 7, fontSize: 12.5,
          border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--txt2)",
        }}
      >
        Cancelar
      </button>

      {erro && (
        <p role="alert" style={{ flexBasis: "100%", margin: 0, fontSize: 12, color: "var(--erro)" }}>{erro}</p>
      )}
    </form>
  )
}

/** O campo e o rótulo no mesmo desenho da ficha do projeto (`DadosDoProjeto`), para não destoar. */
const entrada: React.CSSProperties = {
  width: "100%", padding: "0 9px", borderRadius: 7,
  border: "1px solid var(--line2)", background: "var(--surface2)", fontSize: 12.5,
}

function Campo({ rotulo, largura, children }: { rotulo: string; largura: number; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: `1 1 ${largura}px`, maxWidth: largura * 1.4 }}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>{rotulo}</span>
      {children}
    </label>
  )
}
